import { isInternalUrl } from '@bingads-webui/url-util';
import { AjaxStub, AjaxEventHandler, ErrorStub } from '@bingads-webui/instrumentation-stubs';
import { getInstrumentation as getInstrumentationInstance } from '@bingads-webui-campaign/instrumentation-setup';
import {
  OdataErrorMapper,
  CampaignApiErrorMapper,
  UrlErrorMapper,
  odataErrorCodes,
  campaignApiErrorCodes,
  invalidDomains,
} from '@bingads-webui-campaign/instrumentation';
import type { AjaxContext } from '@bingads-webui-campaign/instrumentation-setup';
import { pmaxLiteSchemas } from './pmax-lite-schemas';
import type {
  GetInstrumentationOptions,
  GetInstrumentationServiceOptions,
  InstrumentationService,
  PMaxLiteInstrumentation,
  PMaxLiteServerContext,
} from './types';

export const SUB_SYSTEM_NAMES = {
  PMaxLiteSignup: 'pmax-lite-signup',
};

const errorMappers = {
  odataErrorMapper: new OdataErrorMapper(odataErrorCodes),
  campaignApiErrorMapper: new CampaignApiErrorMapper(campaignApiErrorCodes),
  urlErrorMapper: new UrlErrorMapper(invalidDomains),
};

function stub(instrumentation: PMaxLiteInstrumentation, serverContext: PMaxLiteServerContext) {
  const stubOptions = {
    pageTrackingId: serverContext && serverContext.PageTrackingId,
    lcid: serverContext && serverContext.CultureLcid,
  };

  const shouldAddHeaders = (ajaxContext: AjaxContext) => isInternalUrl(ajaxContext.url);
  const eventHandler = new AjaxEventHandler({
    errorMappers,
    shouldAddHeaders,
  });

  const ajaxStub = new AjaxStub({
    stubOptions,
    eventHandler,
    instrumentation,
  });

  const errorStub = new ErrorStub({
    instrumentation,
  });

  ajaxStub.init();
  errorStub.init();
}

export const getInstrumentationService = ({
  instrumentation,
  serverContext,
}: GetInstrumentationServiceOptions): InstrumentationService => {
  instrumentation.addScenario(pmaxLiteSchemas);
  const defaultScenario = instrumentation.defaultScenario.create();
  const logger = defaultScenario.logger.create();

  stub(instrumentation, serverContext);

  return {
    instrumentation,
    logger,
  };
};

export const getInstrumentation = ({
  serverContext,
  devConfig,
  subSystemName = SUB_SYSTEM_NAMES.PMaxLiteSignup,
}: GetInstrumentationOptions): PMaxLiteInstrumentation => {
  const {
    CurrentCustomer,
    CurrentAccount,
    CurrentUser,
  } = serverContext;

  const userContext = {
    CurrentCustomer: CurrentCustomer || {},
    CurrentAccount: CurrentAccount || {},
    CurrentUser: CurrentUser || {},
  };

  return getInstrumentationInstance({
    pageTrackingId: serverContext.PageTrackingId,
    errorMappers,
    isInDevelopmentMode: devConfig.isInDevelopmentMode,
    userContext,
    applicationName: {
      systemName: 'campaign',
      subSystemName,
    },
    serverContext: {
      ...serverContext,
      ...userContext,
    },
    objectsToStub: [],
  }) as PMaxLiteInstrumentation;
};
